import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const sections = [
  {
    title: 'Introduction',
    content: [
      {
        text: 'These Terms of Service (“Terms”) govern the use of services provided by Reano AI Technologies, a company incorporated under the laws of India in Haryana (hereinafter referred to as the “Company”, “Reano AI”, “we”, “our”, or “us”).',
      },
      {
        text: 'Reano AI provides a Software-as-a-Service (SaaS) platform that enables clients with AI-powered customer support chatbots, primarily on WhatsApp and websites, along with supporting dashboards, APIs, and automation tools (collectively, the “Service”).',
      },
      {
        text: 'By registering, accessing, or using the Service, you (“User”, “Client”, or “you”) agree to be bound by these Terms.',
      },
    ],
  },
  {
    title: 'Nature of the Service',
    content: [
      {
        text: 'Reano AI enables businesses and individuals to:',
      },
      {
        list: [
          'Deploy AI chatbots for customer support and engagement',
          'Automate conversations via WhatsApp and web interfaces',
          'Train AI models using user-provided data',
          'Integrate APIs and third-party tools',
        ],
      },
      {
        subtitle: '⚠️ Important disclaimer',
        text: 'The Service uses artificial intelligence and automation. Responses generated by the chatbot may not always be accurate, complete, or suitable for all situations.',
      },
    ],
  },
  {
    title: 'WhatsApp & Third-Party Services',
    content: [
      {
        text: 'Reano AI integrates with third-party platforms, including but not limited to WhatsApp (via third-party providers).',
      },
      {
        text: 'Reano AI is not affiliated with or endorsed by Meta (WhatsApp).',
      },
      {
        list: [
          'Users must comply with all applicable WhatsApp Business and messaging policies',
          'Any violation (spam, unauthorized messaging, etc.) is solely the User’s responsibility',
        ],
      },
      {
        text: 'Reano AI shall not be liable for:',
      },
      {
        list: [
          'WhatsApp account bans or restrictions',
          'Message delivery failures caused by third-party providers',
          'Policy violations by the User',
        ],
      },
    ],
  },
  {
    title: 'Registration and Account',
    content: [
      {
        text: 'To use the Service, you must:',
      },
      {
        list: [
          'Provide accurate and complete information',
          'Maintain confidentiality of login credentials and API keys',
          'Be responsible for all activities under your account',
        ],
      },
      {
        text: 'Reano AI may send service-related communications (onboarding, updates, billing, etc.) which are essential and cannot be opted out of while using the Service.',
      },
    ],
  },
  {
    title: 'Free Trial and Subscription',
    content: [
      {
        list: [
          'Reano AI may offer a free trial with limited features',
          'Paid plans are subscription-based, billed in advance',
          'Pricing may be available on an as-you-go basis, solely at the discretion of Reano AI',
          'Pricing is available on official channels or shared directly, or finalized in a written agreement',
        ],
      },
      {
        text: 'Failure to make payment may result in:',
      },
      {
        list: [
          'Suspension or limitation of services',
          'Permanent termination after notice',
        ],
      },
    ],
  },
  {
    title: 'User Responsibilities',
    content: [
      {
        text: 'You agree NOT to use the Service for:',
      },
      {
        list: [
          'Sending spam or unsolicited messages',
          'Violating WhatsApp or any third-party policies',
          'Sharing illegal, harmful, or misleading content',
          'Impersonation or fraudulent activity',
        ],
      },
      {
        text: 'You are solely responsible for:',
      },
      {
        list: [
          'All chatbot interactions with your customers',
          'Content, data, and training material you provide',
          'Compliance with applicable laws (including data protection and communication laws)',
        ],
      },
    ],
  },
  {
    title: 'AI Training and Data Usage',
    content: [
      {
        text: 'AI training on your data is not optional.',
      },
      {
        text: 'By enabling training, you grant Reano AI a limited license to process your data solely for improving chatbot performance.',
      },
      {
        text: 'Reano AI:',
      },
      {
        list: [
          'Does not claim ownership of your data',
          'Uses data only for providing and improving the Service',
        ],
      },
      {
        subtitle: '⚠️ You must ensure',
      },
      {
        list: [
          'You have rights to use and upload all data',
          'No sensitive or restricted data is shared without consent',
        ],
      },
    ],
  },
  {
    title: 'Intellectual Property',
    content: [
      {
        list: [
          'Reano AI retains all rights to its platform, software, and technology',
          'Users retain ownership of their own content and data',
          'You grant Reano AI a non-exclusive, worldwide, royalty-free license to use your content strictly for service delivery',
        ],
      },
    ],
  },
  {
    title: 'AI Disclaimer',
    content: [
      {
        text: 'The User acknowledges that:',
      },
      {
        list: [
          'AI-generated responses may contain errors, inaccuracies, or outdated information',
          'Chatbots operate based on training data and automated logic',
          'Responses do not constitute professional advice (legal, financial, medical, etc.)',
        ],
      },
      {
        text: 'Reano AI shall not be liable for:',
      },
      {
        list: [
          'Business losses',
          'Customer dissatisfaction',
          'Miscommunication caused by chatbot responses',
          'Outdated information where the client has not notified Reano AI in writing via official communication channels',
        ],
      },
    ],
  },
  {
    title: 'Service Availability',
    content: [
      {
        text: 'We do not guarantee:',
      },
      {
        list: [
          'Uninterrupted or error-free operation',
          'Real-time response delivery',
          'Continuous availability (due to maintenance or external dependencies)',
        ],
      },
    ],
  },
  {
    title: 'Limitation of Liability',
    content: [
      {
        text: 'To the maximum extent permitted by law:',
      },
      {
        list: [
          'Reano AI shall not be liable for any indirect, incidental, or consequential damages',
          'Total liability shall not exceed the fees paid in the last 2 months',
        ],
      },
      {
        text: 'This includes (but is not limited to):',
      },
      {
        list: [
          'Loss of revenue or business',
          'Data loss',
          'Third-party failures (WhatsApp, APIs, hosting providers)',
        ],
      },
    ],
  },
  {
    title: 'Suspension and Termination',
    content: [
      {
        text: 'Reano AI may suspend or terminate your account if:',
      },
      {
        list: [
          'You violate these Terms',
          'You misuse the platform',
          'Required payments are not made',
        ],
      },
      {
        text: 'You may terminate your account at any time as per the cancellation policy.',
      },
    ],
  },
  {
    title: 'Changes to Terms',
    content: [
      {
        text: 'Reano AI reserves the right to update these Terms.',
      },
      {
        list: [
          'Users will be notified of material changes',
          'Continued use after updates constitutes acceptance',
        ],
      },
    ],
  },
  {
    title: 'Governing Law',
    content: [
      {
        text: 'These Terms shall be governed by the laws of India, with jurisdiction in Haryana courts.',
      },
    ],
  },
];

export default function TermsOfService() {
  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>

      <div style={{ background: 'linear-gradient(135deg, #479EC3 0%, #3B88A9 100%)', padding: '48px 0 52px' }}>
        <div className="container">
          <Link to="/" style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            fontSize: 13.5, fontWeight: 500, color: 'rgba(255,255,255,0.8)',
            textDecoration: 'none', marginBottom: 28, transition: 'color .15s',
          }}
            onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M10 3L5 8l5 5"/>
            </svg>
            Back to Home
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          >
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              fontSize: 11.5, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 100,
              padding: '4px 14px', marginBottom: 18,
            }}>
              Legal
            </div>
            <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 800, letterSpacing: '-.03em', color: '#fff', marginBottom: 12 }}>
              Terms of Service
            </h1>
            <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.78)' }}>
              Effective date: <strong style={{ color: '#fff' }}>1 April 2026</strong>&nbsp;·&nbsp;Reano AI Technologies
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container" style={{ padding: '64px 28px 96px' }}>
        <div style={{ maxWidth: 780 }}>

          {sections.map((sec, i) => (
            <motion.div
              key={sec.title}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.1 + i * 0.04 }}
              style={{
                background: '#fff', borderRadius: 16, padding: '32px',
                border: '1px solid #E2E8F0', marginBottom: 16,
              }}
            >
              <h2 style={{
                fontSize: 17, fontWeight: 700, color: '#0F2335',
                letterSpacing: '-.02em', marginBottom: 20,
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: 8,
                  background: 'rgba(71,158,195,0.1)', color: '#479EC3',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 800, flexShrink: 0,
                }}>{String(i + 1).padStart(2, '0')}</span>
                {sec.title}
              </h2>

              {sec.content.map((block, j) => (
                <div key={j} style={{ marginBottom: j < sec.content.length - 1 ? 16 : 0 }}>
                  {block.subtitle && (
                    <div style={{ fontSize: 13.5, fontWeight: 600, color: '#479EC3', marginBottom: 8 }}>
                      {block.subtitle}
                    </div>
                  )}
                  {block.highlight && (
                    <p style={{ fontSize: 14.5, fontWeight: 600, color: '#0F2335', marginBottom: 8 }}>
                      {block.highlight}
                    </p>
                  )}
                  {block.text && (
                    <p style={{ fontSize: 14.5, color: '#4A6070', lineHeight: 1.75, marginBottom: block.list ? 12 : 0 }}>
                      {block.text}
                    </p>
                  )}
                  {block.list && (
                    <ul style={{ paddingLeft: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {block.list.map((item, k) => (
                        <li key={k} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14.5, color: '#4A6070', lineHeight: 1.65 }}>
                          <span style={{
                            width: 6, height: 6, borderRadius: '50%', background: '#479EC3',
                            flexShrink: 0, marginTop: 8,
                          }}/>
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
